import { useEffect, useMemo, useRef, useState } from 'react'

import { laborFrom, materialPrice, priceAsOf } from '../../lib/pricing'
import { productTitle } from '../../lib/product'
import { BUSINESS_TIMEZONE } from '../../lib/datetime'
import type { ProductCard } from '../../lib/wstypes'

import { ViewInSpace } from './ViewInSpace'

/**
 * The product rail (01 §3.5, 04 §4).
 *
 * Every card is a real, purchasable item with a current price and a link to the store page.
 * That is the whole promise of the product, so the card states its terms plainly:
 *
 *   * the **material price** as the store has it, with the date it was captured;
 *   * **"Estimated labor from"**, or "Estimated on site" when Andrew has not set that rate —
 *     never a made-up number and never a total (01 §7);
 *   * **where it comes from**, linked (06 §3.2).
 *
 * A rail that returns nothing says so and offers the custom path, because 04 §4 makes the
 * agent's honesty depend on an empty result actually being empty.
 */

interface Props {
  slot: string | null
  slotName: string | null
  products: ProductCard[]
  loading: boolean
  zeroReason: string | null
  activeProductId: string | null
  onTry: (product: ProductCard) => void
  onAdd: (product: ProductCard) => void
  onSearch: (query: string) => void
  onCustom: () => void
  query: string
  disabled?: boolean
}

export function ProductRail({
  slot,
  slotName,
  products,
  loading,
  zeroReason,
  activeProductId,
  onTry,
  onAdd,
  onSearch,
  onCustom,
  query,
  disabled,
}: Props) {
  const [draft, setDraft] = useState(query)
  const railRef = useRef<HTMLUListElement>(null)

  useEffect(() => setDraft(query), [query])

  const heading = slotName ?? 'Products'

  return (
    <section aria-labelledby="rail-heading" className="flex min-h-0 min-w-0 flex-col">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="rail-heading" className="text-lg">
          {heading}
        </h2>
        {products.length > 0 && (
          <span className="font-mono text-xs uppercase tracking-[0.12em] text-muted">
            {products.length} option{products.length === 1 ? '' : 's'}
          </span>
        )}
      </div>

      <form
        className="mt-3 flex gap-2"
        onSubmit={(event) => {
          event.preventDefault()
          onSearch(draft.trim())
        }}
      >
        <input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder={slot ? `Describe what you want…` : 'Search products…'}
          aria-label="Describe what you want"
          className="min-h-11 min-w-0 flex-1 rounded-btn border border-line bg-white px-3.5 text-base text-ink placeholder:text-muted"
        />
        <button
          type="submit"
          className="min-h-11 rounded-btn border border-line bg-white px-4 text-sm font-semibold text-blue-deep"
        >
          Search
        </button>
      </form>

      {loading && <p className="mt-6 text-muted">Finding options…</p>}

      {!loading && products.length === 0 && (
        <div className="mt-6 rounded-card border border-line bg-white p-5">
          <p className="m-0 text-ink">
            {/* The server says which constraint emptied the result so the agent can answer
                honestly (04 §4); a homeowner just needs the plain sentence, with the name of
                the thing rather than its slot id. */}
            {zeroReason?.startsWith('nothing matched')
              ? `Nothing matches those filters${slotName ? ` for the ${slotName.toLowerCase()}` : ''}.`
              : `We don't have approved ${slotName ? `${slotName.toLowerCase()} options` : 'products for this'} yet.`}
          </p>
          <p className="m-0 mt-2 text-sm text-muted">
            Andrew can still do it — describe what you have in mind and it goes on the list as
            “Custom — estimated on site”.
          </p>
          <button
            type="button"
            onClick={onCustom}
            className="mt-4 min-h-11 rounded-btn bg-navy px-5 font-semibold text-white"
          >
            Describe what you want
          </button>
        </div>
      )}

      <ul
        ref={railRef}
        className="m-0 mt-4 grid list-none grid-cols-1 gap-3 overflow-y-auto p-0 sm:grid-cols-2 xl:grid-cols-1"
      >
        {products.map((product, index) => (
          <li
            key={product.id}
            className="anim-rise"
            style={{ animationDelay: `${Math.min(index * 45, 360)}ms` }}
          >
            <Card
              product={product}
              active={product.id === activeProductId}
              onTry={() => onTry(product)}
              onAdd={() => onAdd(product)}
              disabled={disabled}
            />
          </li>
        ))}
      </ul>
    </section>
  )
}

function Card({
  product,
  active,
  onTry,
  onAdd,
  disabled,
}: {
  product: ProductCard
  active: boolean
  onTry: () => void
  onAdd: () => void
  disabled?: boolean
}) {
  const asOf = useMemo(
    () => priceAsOf(product.price_as_of, BUSINESS_TIMEZONE),
    [product.price_as_of],
  )

  return (
    <article
      className={`overflow-hidden rounded-card border bg-white transition-colors ${
        active ? 'border-blue shadow-blue' : 'border-line'
      }`}
    >
      <div className="flex gap-3 p-3">
        {product.image_url ? (
          <img
            src={product.image_url}
            alt=""
            loading="lazy"
            className="size-24 shrink-0 rounded-btn object-cover"
          />
        ) : (
          <div className="grid size-24 shrink-0 place-items-center rounded-btn bg-mist text-xs text-muted">
            No photo
          </div>
        )}

        <div className="min-w-0 flex-1">
          {product.preferred && (
            <span className="mb-1 inline-block rounded-pill bg-blue-tint px-2 py-0.5 font-mono text-xs font-semibold uppercase tracking-[0.1em] text-blue-deep">
              Andrew recommends
            </span>
          )}
          {/* Trimmed for reading, not for the record: the full catalogue name is on the
              element and goes to Andrew unchanged. See lib/product.ts. */}
          <h3 className="text-sm font-semibold leading-snug text-ink" title={product.name}>
            {productTitle(product.name, product.brand)}
          </h3>
          {product.brand && <p className="m-0 text-xs text-muted">{product.brand}</p>}

          <p className="m-0 mt-1.5 text-sm font-semibold text-ink">
            {materialPrice(product.material_price)}
          </p>
          <p className="m-0 text-xs text-muted">
            {product.attribution.store} price as of {asOf}
            {product.price_unverified && ' · price unverified'}
          </p>
          <p className="m-0 mt-0.5 text-xs text-body">{laborFrom(product.labor_from)}</p>

          {product.reasons.length > 0 && (
            <p className="m-0 mt-1 text-xs italic text-muted">{product.reasons[0]}</p>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-t border-line px-3 py-2.5">
        <button
          type="button"
          onClick={onTry}
          disabled={disabled}
          className="min-h-11 rounded-btn bg-blue px-4 text-sm font-semibold text-white disabled:opacity-50"
        >
          Try it
        </button>
        <button
          type="button"
          onClick={onAdd}
          disabled={disabled}
          className="min-h-11 rounded-btn border border-line bg-white px-4 text-sm font-semibold text-blue-deep disabled:opacity-50"
        >
          Add to list
        </button>
        <ViewInSpace product={product} />
        <a
          href={product.attribution.url}
          target="_blank"
          rel="noreferrer noopener"
          className="ml-auto min-h-11 self-center text-xs font-semibold text-muted underline underline-offset-2"
        >
          At {product.attribution.store}
        </a>
      </div>
    </article>
  )
}
